import {Button, DataStatus, openDialog} from 'platform/components';
import {Box, VStack, HStack, Show} from 'platform/foundation';

import {isFalse, isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useGetBusinessCaseActivityListQuery,
  useGetBusinessCaseQuery,
  useGetBusinessCaseUpcomingTasksQuery,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {
  ActivitySidebarSendMessage,
  getUpcomingTestDrives,
  LogContactDialog,
  transformTaskToActivity,
} from '@dms/features/activity-panel';
import {openCreateTaskFormDialog} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {usePermissions} from '@dms/shared';

import {getPurchaseVehicleIdFromBusinessCase} from '../../../utils/getPurchaseVehicleIdFromBusinessCase';
import {getSaleVehicleIdFromBusinessCase} from '../../../utils/getSaleVehicleIdFromBusinessCase';
import {ActivityList} from './ActivityList';
import {EmptyState} from './EmptyState';

const SCROLLBAR_GUTTER_PADDING = 3;
const SCROLLBAR_GUTTER_WIDTH = 'calc(100% + 0.75rem)';

interface ActivityTabProps {
  businessCaseId: string;
}

export function ActivityTab(props: ActivityTabProps) {
  const [canReadTasks, canCreateTasks] = usePermissions({
    permissionKeys: ['readTask', 'createTask'],
  });

  // The customer's online-offer/chat actions change these server-side with no client signal,
  // so re-opening the tab or refocusing the window refetches them.
  const {
    data: businessCaseData,
    isLoading: isBusinessCaseLoading,
    isError: isBusinessCaseErrored,
  } = useGetBusinessCaseQuery(
    {businessCaseId: props.businessCaseId},
    {
      skip: isNilOrEmpty(props.businessCaseId),
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    }
  );

  const {
    data,
    isLoading: isBusinessCaseActivityLoading,
    isError: isBusinessCaseActivityErrored,
  } = useGetBusinessCaseActivityListQuery(
    {businessCaseId: props.businessCaseId},
    {
      skip: isNilOrEmpty(props.businessCaseId),
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    }
  );

  const {
    data: upcomingTasks,
    isLoading: isUpcomingTasksLoading,
    isError: isUpcomingTasksErrored,
  } = useGetBusinessCaseUpcomingTasksQuery(
    {businessCaseId: props.businessCaseId},
    {skip: isNilOrEmpty(props.businessCaseId)}
  );

  const handleNewTaskClick = () => {
    openCreateTaskFormDialog({
      'data-testid': 'business-case-activity-tab-create-task',
      relatedRecordId: props.businessCaseId,
      resourceId: EntityResourceIds.businessCase,
    });
  };

  const vehicleId =
    getSaleVehicleIdFromBusinessCase(businessCaseData) ??
    getPurchaseVehicleIdFromBusinessCase(businessCaseData) ??
    undefined;

  const upcomingTestDrives = getUpcomingTestDrives(data ?? []);
  const upcomingTestDriveIds = new Set(upcomingTestDrives.map((td) => td.id));
  const pastActivities = data?.filter((activity) => !upcomingTestDriveIds.has(activity.id)) ?? [];
  const isAddNewTaskEnabled = canReadTasks && canCreateTasks;
  const isLoading =
    isBusinessCaseLoading || isBusinessCaseActivityLoading || isUpcomingTasksLoading;
  const isError = isBusinessCaseErrored || isBusinessCaseActivityErrored || isUpcomingTasksErrored;

  if (isNilOrEmpty(data) && !isLoading && !isError) {
    return <EmptyState businessCaseId={props.businessCaseId} />;
  }

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <VStack spacing={4} grow={1} minHeight={0}>
        <HStack spacing={2}>
          <ActivitySidebarSendMessage
            resourceId={EntityResourceIds.businessCase}
            recordId={props.businessCaseId}
            customerId={businessCaseData?.customerId}
            vehicleId={vehicleId}
          />

          <Button
            leftIcon="content/add_circle"
            title={i18n.t('entity.task.actions.createTask')}
            variant="link"
            size="small"
            isDisabled={isFalse(isAddNewTaskEnabled)}
            onClick={handleNewTaskClick}
          />

          <Button
            leftIcon="communication/phone"
            title={i18n.t('entity.activityPanel.activity.logCall')}
            onClick={() =>
              openDialog(<LogContactDialog businessCaseId={props.businessCaseId} />, {
                id: 'LOG_CONTACT_DIALOG',
                title: i18n.t('entity.activityPanel.logContact.createLog'),
                scrollBehavior: 'outside',
              })
            }
            variant="link"
            size="small"
          />
        </HStack>

        <Box
          flex={1}
          minHeight={0}
          overflowX="hidden"
          overflowY="auto"
          width={SCROLLBAR_GUTTER_WIDTH}
          paddingRight={SCROLLBAR_GUTTER_PADDING}
        >
          <VStack spacing={4}>
            <ActivityList
              showEmpty
              label={i18n.t('entity.activityPanel.activity.nextSteps')}
              items={[...(upcomingTasks ?? []).map(transformTaskToActivity), ...upcomingTestDrives]}
            />

            <Show when={isNotNilOrEmpty(pastActivities)}>
              <ActivityList
                label={i18n.t('entity.activityPanel.activity.pastActivities')}
                items={pastActivities}
              />
            </Show>
          </VStack>
        </Box>
      </VStack>
    </DataStatus>
  );
}
